// What an address that goes nowhere gets: our robot in pieces, and a way back.
//
// Hosting sends every unknown path to the app (firebase.json), so the 404 is
// ours to draw. Signed in, it sits inside the dashboard's own header and
// footer; signed out, it brings a plain header of its own, like the legal
// pages. Either way it asks search engines not to index it.

import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ColorModeToggle } from "../components/ColorModeToggle";
import Footer from "../components/Footer";
import { Logo } from "../components/Logo";
import { PRODUCT_NAME } from "../lib/brand";

export const NOT_FOUND_TITLE = `Page not found | ${PRODUCT_NAME}`;

function useNotFoundHead() {
  useEffect(() => {
    const title = document.title;
    document.title = NOT_FOUND_TITLE;
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex";
    document.head.appendChild(robots);
    return () => {
      document.title = title;
      robots.remove();
    };
  }, []);
}

function Body({ signedIn }: { signedIn: boolean }) {
  const { pathname } = useLocation();
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center py-6 text-center">
      <img
        src="/hero/robot-in-pieces.webp"
        width={360}
        height={360}
        alt="The Build Bots Now robot sitting in a heap of its own parts, with dizzy spiral eyes, a bent antenna and a few loose bolts."
        className="h-auto w-64 rounded-3xl sm:w-80"
      />
      <h1 className="mt-6 text-2xl font-semibold text-slate-900 sm:text-3xl">This page fell to pieces</h1>
      <p className="mt-3 max-w-md break-words text-slate-600">
        There is nothing at <span className="font-mono text-sm text-slate-800">{pathname}</span>. The link may be old, or
        mistyped.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {signedIn ? (
          <Link
            to="/agents"
            className="rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            Back to your agents
          </Link>
        ) : (
          <>
            <Link
              to="/"
              className="rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
            >
              Go to the home page
            </Link>
            <Link
              to="/signin"
              className="rounded-xl border border-slate-300 bg-surface px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

// Inside the dashboard's <Layout>, which brings the header and footer.
export function NotFoundInApp() {
  useNotFoundHead();
  return <Body signedIn />;
}

// On its own, for a visitor who is not signed in.
export default function NotFound() {
  useNotFoundHead();
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" aria-label={`${PRODUCT_NAME} home`} className="flex">
            <Logo />
          </Link>
          <ColorModeToggle />
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-10">
        <Body signedIn={false} />
      </main>
      <Footer />
    </div>
  );
}
